import React from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { translateBlockValue } from 'i18n';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { Box } from '@mui/material';
import {
    StyHomeFilters,
    StyHomeFiltersWrapper,
} from './StyledComponentBudgetFilters';
import { getSortedBudgetNameOptions } from '../helper';
import BudgetAutocomplete from '../../BudgetAutocomplete/BudgetAutocomplete';
import { BudgetSelectionT } from '../types';
import MultiSelectWithCheckboxes from 'ui/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';
import { useUpdateBudgetDisplaySettings } from '../hooks/budgetDisplayHooks';

type Props = {
    selectedBudgetSoftwareIds: string[];
    setSelectedBudgetSoftwareIds: (arg1: string[]) => void;
    budgetNamesByYear: Record<number, BudgetSelectionT[]>;
    selectedExercice: number;
    setSelectedExercice: (arg1: string) => void;
    exerciceOptions: number[];
};

export default function BudgetLibraryFilters({
    selectedBudgetSoftwareIds,
    setSelectedBudgetSoftwareIds,
    budgetNamesByYear,
    selectedExercice,
    setSelectedExercice,
    exerciceOptions,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const { mutate: updateBudgetDisplaySettings } =
        useUpdateBudgetDisplaySettings(setSelectedBudgetSoftwareIds);

    const trackFilterChange = (filter: string) => {
        trackEventBudget(TRACKABLE_EVENTS.budget.ux.homeFiltersChanged, {
            filter,
        });
    };

    const handleBudgetChange = (newValues: string[]) => {
        const addedValues = newValues.filter(
            (value) => !selectedBudgetSoftwareIds.includes(value),
        );
        const removedValues = selectedBudgetSoftwareIds.filter(
            (value) => !newValues.includes(value),
        );
        const updatedBudgetSettings = [
            ...addedValues.map((value) => ({
                exercice: selectedExercice,
                budgetSoftwareId: value,
                budgetDisplayPosition: -1,
            })),
            ...removedValues.map((value) => ({
                exercice: selectedExercice,
                budgetSoftwareId: value,
                budgetDisplayPosition: null,
            })),
        ];
        updateBudgetDisplaySettings({ body: updatedBudgetSettings, newValues });
        trackFilterChange(translateBlockValue('budget.name'));
    };

    const handleExerciceChange = (newValue: number) => {
        setSelectedExercice(newValue.toString());
        trackFilterChange(translateBlockValue('budgetLine.year'));
    };

    const budgetOptionsforSelectedExercice =
        budgetNamesByYear[selectedExercice];
    const sortedBudgetNamesOptions = getSortedBudgetNameOptions(
        budgetOptionsforSelectedExercice,
    );
    const sortedExercices = [...exerciceOptions].sort((a, b) => b - a);

    return (
        <Box>
            <StyHomeFiltersWrapper>
                <Typography variant="h5">
                    {t('budgetProposal.selectFilter')}
                </Typography>
            </StyHomeFiltersWrapper>
            <StyHomeFiltersWrapper>
                <StyHomeFilters>
                    <MultiSelectWithCheckboxes
                        sx={{ width: '250px', mr: '16px' }}
                        label={translateBlockValue('budget.name')}
                        value={selectedBudgetSoftwareIds}
                        onChange={handleBudgetChange}
                        options={sortedBudgetNamesOptions}
                    />
                    <BudgetAutocomplete
                        disableClearable
                        sx={{
                            verticalAlign: 'bottom',
                            display: 'inline-flex',
                            p: 0,
                        }}
                        options={sortedExercices}
                        value={selectedExercice}
                        onChange={handleExerciceChange}
                        labelField={t('common.year')}
                    />
                </StyHomeFilters>
            </StyHomeFiltersWrapper>
        </Box>
    );
}
